import { Injectable } from '@angular/core';

import { merge, Observable, tap } from 'rxjs';

import { BlockedTime } from '@shared/blocked-time/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { ApiClientService } from '@tablesready/api-client';
import {
  BlockedTimeQuery,
  BlockedTimeStore,
  SocketService,
} from '@tablesready/frontend-store';

@Injectable({ providedIn: 'root' })
export class BlockedTimeApiService {
  private get basePath(): string {
    return '/v.1/blocked-time';
  }

  constructor(
    private readonly store: BlockedTimeStore,
    private readonly query: BlockedTimeQuery,
    private readonly http: ApiClientService,
    private readonly socketService: SocketService,
  ) {}

  load(): Observable<BlockedTime[]> {
    return this.http
      .get<BlockedTime[]>(this.basePath)
      .pipe(tap((blockedTimes) => {
        this.store.set(blockedTimes);
      }));
  }

  selectAll(): Observable<BlockedTime[]> {
    return this.query.selectAll();
  }

  create(data: Partial<BlockedTime>): Observable<BlockedTime> {
    return this.http
      .post<BlockedTime>(this.basePath, data)
      .pipe(tap((blockedTime) => {
        this.store.add(blockedTime);
      }));
  }

  updateById(id: string, data: Partial<BlockedTime>): Observable<BlockedTime> {
    return this.http
      .put<BlockedTime>(`${this.basePath}/${id}`, data)
      .pipe(tap((blockedTime) => {
        this.store.update(id, blockedTime);
      }));
  }

  deleteById(id: string): Observable<unknown> {
    return this.http
      .delete(`${this.basePath}/${id}`)
      .pipe(tap(() => {
        this.store.remove(id);
      }));
  }

  listenToChanges(): Observable<BlockedTime[] | string[]> {
    return merge(
      this.socketService
        .getEventStream<BlockedTime[]>(WebSocketEventsEnum.blockedTimeCreated)
        .pipe(tap((blockedTimes) => {
          this.store.add(blockedTimes);
        }),
      ),
      this.socketService
        .getEventStream<BlockedTime[]>(WebSocketEventsEnum.blockedTimeUpdated)
        .pipe(
          tap((blockedTimes) => {
            for (const blockedTime of blockedTimes) {
              this.store.update(blockedTime._id, blockedTime);
            }
          }),
      ),
      this.socketService
        .getEventStream<string[]>(WebSocketEventsEnum.blockedTimeDeleted)
        .pipe(tap((ids) => {
          this.store.remove(ids);
      })),
    );
  }

  reset(): void {
    this.store.reset();
  }
}
